/**
 * FILE: apps/hub/src/lib/auth.ts
 * PURPOSE: Utility / configuration module.
 * AGENT_CONTEXT: Shared logic. Keep pure when possible. Export from index if public.
 * LAST_REVIEWED: 2026-05-07
 */

import type { Session } from '@supabase/supabase-js'
import { supabase } from '@fintree/supabase/shared'
import type { AuthIdentity } from '@/types/auth'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? ''

export async function getSession() {
  const { data, error } = await supabase.auth.getSession()
  if (error) throw error
  return data.session
}

interface HubAuthBundle {
  staff: {
    id: string
    email: string
    name: string | null
    role: string
    active: boolean
    [key: string]: unknown
  }
  permissions: string[]
  roleName: string
  requires_password_reset?: boolean
}

async function fetchHubAuthBundle(session: Session): Promise<HubAuthBundle> {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/hub-auth-bundle`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Auth bundle request failed' }))
    throw new Error(err.error ?? `Auth bundle failed (${res.status})`)
  }

  const raw = await res.text()
  return JSON.parse(raw) as HubAuthBundle
}

async function buildIdentity(session: Session): Promise<AuthIdentity | null> {
  const email = session.user.email?.trim()
  if (!email) return null
  try {
    const bundle = await fetchHubAuthBundle(session)
    return {
      user: session.user,
      staffId: bundle.staff.id,
      name: bundle.staff.name ?? session.user.email ?? 'Unknown user',
      permissions: bundle.permissions,
      roleName: bundle.roleName,
      requiresPasswordReset:
        bundle.requires_password_reset ??
        ((bundle.staff as Record<string, unknown>)?.requires_password_reset as boolean | undefined) ??
        false,
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : ''
    if (msg.includes('Staff account not found') || msg.includes('Auth bundle failed')) return null
    throw err
  }
}

export async function signIn(email: string, password: string): Promise<AuthIdentity> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  if (!data.session) throw new Error('No active session created.')
  const identity = await buildIdentity(data.session)
  if (!identity) {
    await supabase.auth.signOut()
    throw new Error('Staff profile not found for this account.')
  }
  return identity
}

export async function getIdentityFromSession(): Promise<AuthIdentity | null> {
  const session = await getSession()
  if (!session) return null
  return buildIdentity(session)
}

export async function signOut() {
  const { error } = await supabase.auth.signOut()
  if (error) throw error
}
